import {
	createApiRef,
	DiscoveryApi,
	FetchApi,
} from "@backstage/core-plugin-api";
import { ResponseError } from "@backstage/errors";

export interface WorkflowMetadata {
	name?: string;
	namespace?: string;
	uid?: string;
	creationTimestamp?: string;
	labels?: Record<string, string>;
	annotations?: Record<string, string>;
}

export interface WorkflowNodeStatus {
	id: string;
	name?: string;
	displayName?: string;
	type?: string;
	phase?: string;
	startedAt?: string;
	finishedAt?: string;
	message?: string;
	templateName?: string;
	children?: string[];
	outboundNodes?: string[];
	inputs?: unknown;
	outputs?: { exitCode?: string };
}

export interface Workflow {
	metadata: WorkflowMetadata;
	spec?: { entrypoint?: string; workflowTemplateRef?: { name?: string } };
	status?: {
		phase?: string;
		message?: string;
		startedAt?: string;
		finishedAt?: string;
		progress?: string;
		nodes?: Record<string, WorkflowNodeStatus>;
	};
}

export interface WorkflowTemplate {
	metadata: WorkflowMetadata;
	spec?: { entrypoint?: string };
}

export interface WorkflowList {
	items?: Workflow[] | null;
}

export interface WorkflowTemplateList {
	items?: WorkflowTemplate[] | null;
}

export interface ArgoWorkflowsApi {
	listWorkflows(opts: {
		namespace: string;
		labelSelector?: string;
		limit?: number;
	}): Promise<Workflow[]>;
	getWorkflow(opts: { namespace: string; name: string }): Promise<Workflow>;
	listTemplates(opts: {
		namespace: string;
		labelSelector?: string;
	}): Promise<WorkflowTemplate[]>;
	getLogs(opts: {
		namespace: string;
		name: string;
		podName?: string;
		container?: string;
		tailLines?: number;
	}): Promise<string>;
	retry(opts: { namespace: string; name: string }): Promise<Workflow>;
	resubmit(opts: { namespace: string; name: string }): Promise<Workflow>;
	stop(opts: { namespace: string; name: string }): Promise<Workflow>;
	submit(opts: {
		namespace: string;
		resourceKind:
			| "WorkflowTemplate"
			| "ClusterWorkflowTemplate"
			| "CronWorkflow";
		resourceName: string;
		parameters?: string[];
	}): Promise<Workflow>;
}

export const argoWorkflowsApiRef = createApiRef<ArgoWorkflowsApi>({
	id: "plugin.argo-workflows.service",
});

export class ArgoWorkflowsClient implements ArgoWorkflowsApi {
	private readonly discoveryApi: DiscoveryApi;
	private readonly fetchApi: FetchApi;

	constructor(opts: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
		this.discoveryApi = opts.discoveryApi;
		this.fetchApi = opts.fetchApi;
	}

	private async baseUrl(): Promise<string> {
		return await this.discoveryApi.getBaseUrl("argo-workflows");
	}

	private async request<T>(path: string, init?: RequestInit): Promise<T> {
		const r = await this.fetchApi.fetch(`${await this.baseUrl()}${path}`, init);
		if (!r.ok) throw await ResponseError.fromResponse(r);
		const text = await r.text();
		return text ? (JSON.parse(text) as T) : (undefined as unknown as T);
	}

	private async requestText(path: string, init?: RequestInit): Promise<string> {
		const r = await this.fetchApi.fetch(`${await this.baseUrl()}${path}`, init);
		if (!r.ok) throw await ResponseError.fromResponse(r);
		return r.text();
	}

	async listWorkflows({
		namespace,
		labelSelector,
		limit,
	}: {
		namespace: string;
		labelSelector?: string;
		limit?: number;
	}): Promise<Workflow[]> {
		const qs = new URLSearchParams();
		if (labelSelector) qs.set("labelSelector", labelSelector);
		if (limit) qs.set("limit", String(limit));
		const list = await this.request<WorkflowList>(
			`/workflows/${encodeURIComponent(namespace)}?${qs}`,
		);
		return list.items ?? [];
	}

	async getWorkflow({
		namespace,
		name,
	}: {
		namespace: string;
		name: string;
	}): Promise<Workflow> {
		return await this.request<Workflow>(
			`/workflows/${encodeURIComponent(namespace)}/${encodeURIComponent(name)}`,
		);
	}

	async listTemplates({
		namespace,
		labelSelector,
	}: {
		namespace: string;
		labelSelector?: string;
	}): Promise<WorkflowTemplate[]> {
		const qs = new URLSearchParams();
		if (labelSelector) qs.set("labelSelector", labelSelector);
		const list = await this.request<WorkflowTemplateList>(
			`/workflow-templates/${encodeURIComponent(namespace)}?${qs}`,
		);
		return list.items ?? [];
	}

	async getLogs({
		namespace,
		name,
		podName,
		container,
		tailLines,
	}: {
		namespace: string;
		name: string;
		podName?: string;
		container?: string;
		tailLines?: number;
	}): Promise<string> {
		const qs = new URLSearchParams();
		if (podName) qs.set("podName", podName);
		if (container) qs.set("container", container);
		if (tailLines) qs.set("tailLines", String(tailLines));
		return await this.requestText(
			`/workflows/${encodeURIComponent(namespace)}/${encodeURIComponent(name)}/log?${qs}`,
		);
	}

	async retry(opts: { namespace: string; name: string }): Promise<Workflow> {
		return await this.request<Workflow>(
			`/workflows/${encodeURIComponent(opts.namespace)}/${encodeURIComponent(opts.name)}/retry`,
			{
				method: "PUT",
				headers: { "content-type": "application/json" },
				body: "{}",
			},
		);
	}

	async resubmit(opts: { namespace: string; name: string }): Promise<Workflow> {
		return await this.request<Workflow>(
			`/workflows/${encodeURIComponent(opts.namespace)}/${encodeURIComponent(opts.name)}/resubmit`,
			{
				method: "PUT",
				headers: { "content-type": "application/json" },
				body: "{}",
			},
		);
	}

	async stop(opts: { namespace: string; name: string }): Promise<Workflow> {
		return await this.request<Workflow>(
			`/workflows/${encodeURIComponent(opts.namespace)}/${encodeURIComponent(opts.name)}/stop`,
			{
				method: "PUT",
				headers: { "content-type": "application/json" },
				body: "{}",
			},
		);
	}

	async submit(opts: {
		namespace: string;
		resourceKind:
			| "WorkflowTemplate"
			| "ClusterWorkflowTemplate"
			| "CronWorkflow";
		resourceName: string;
		parameters?: string[];
	}): Promise<Workflow> {
		return await this.request<Workflow>(
			`/workflows/${encodeURIComponent(opts.namespace)}/submit`,
			{
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({
					namespace: opts.namespace,
					resourceKind: opts.resourceKind,
					resourceName: opts.resourceName,
					submitOptions: opts.parameters
						? { parameters: opts.parameters }
						: undefined,
				}),
			},
		);
	}
}
